import React, { useState, useEffect } from 'react';
import { firestore, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  orderBy,
  deleteDoc,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import './AdminDashboard.css';

const SUMMARY_CACHE_KEY = 'coldwire_admin_stats_cache';
const SUMMARY_SESSION_KEY = 'coldwire_admin_stats_synced';

const locationBreakdownConfig = {
  country: {
    label: 'By Country',
    field: 'byCountry',
    empty: 'No country data available.',
  },
  stateProvince: {
    label: 'By State / Province',
    field: 'byStateProvince',
    empty: 'No state or province data available.',
  },
  city: {
    label: 'By City',
    field: 'byCity',
    empty: 'No city data available.',
  },
};

const formatStatsTimestamp = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    return value.toDate().toLocaleString();
  }
  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toLocaleString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
};

const getSortedBreakdown = (data = {}) =>
  Object.entries(data)
      .filter(([, count]) => Number(count) > 0)
      .sort((a, b) => {
        const countDiff = Number(b[1]) - Number(a[1]);
        return countDiff || a[0].localeCompare(b[0]);
      });

const AdminDashboard = () => {
  const [selectedTab, setSelectedTab] = useState('summary');
  const [locationBreakdownTab, setLocationBreakdownTab] = useState('country');
  const [summaryData, setSummaryData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [rebuildingStats, setRebuildingStats] = useState(false);

  const [idReviewCases, setIdReviewCases] = useState([]);
  const [idReviewLoading, setIdReviewLoading] = useState(false);
  const [idReviewError, setIdReviewError] = useState('');
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const [reviewActionLoading, setReviewActionLoading] = useState(null);

  const fetchSummaryData = async () => {
    try {
      const statsRef = doc(firestore, 'admin', 'masterStats');
      const snap = await getDoc(statsRef);

      if (snap.exists()) {
        const data = snap.data();
        setSummaryData(data);
        localStorage.setItem(SUMMARY_CACHE_KEY, JSON.stringify(data));
        sessionStorage.setItem(SUMMARY_SESSION_KEY, 'true');
      } else {
        console.warn('masterStats doc missing');
      }
    } catch (err) {
      console.error('Error fetching summary data:', err);
    }
  };

  const handleRebuildMasterStats = async () => {
    const confirmed = window.confirm(
        'Are you sure you want to trigger a full rebuild of the masterStats in Firestore?',
    );
    if (!confirmed) return;

    try {
      setRebuildingStats(true);
      const rebuild = httpsCallable(functions, 'rebuildMasterStats');
      await rebuild();
      await fetchSummaryData();
    } catch (err) {
      console.error('Error rebuilding master stats:', err);
    } finally {
      setRebuildingStats(false);
    }
  };

  useEffect(() => {
    const cached = localStorage.getItem(SUMMARY_CACHE_KEY);
    if (cached) {
      try {
        setSummaryData(JSON.parse(cached));
      } catch (e) {
        console.error('Failed parsing cached summary:', e);
      }
    }

    if (!sessionStorage.getItem(SUMMARY_SESSION_KEY)) {
      fetchSummaryData();
    }

    const fetchMessages = async () => {
      const q = query(
          collection(firestore, 'contactMessages'),
          orderBy('timestamp', 'desc'),
      );
      const snapshot = await getDocs(q);

      const data = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const msgData = docSnap.data();
            const repliesSnap = await getDocs(
                collection(docSnap.ref, 'replies'),
            );
            const replies = repliesSnap.docs.map((replyDoc) => ({
              id: replyDoc.id,
              ...replyDoc.data(),
            }));
            return {
              id: docSnap.id,
              ...msgData,
              replies,
            };
          }),
      );

      setMessages(data);
    };

    fetchMessages();
  }, []);

  const fetchIdReviewCases = async () => {
    setIdReviewLoading(true);
    setIdReviewError('');
    try {
      const q = query(
          collection(firestore, 'verifications'),
          where('verificationStatus', '==', 'review'),
          orderBy('updatedAt', 'desc'),
      );
      const snap = await getDocs(q);
      const cases = snap.docs.map((docSnap) => ({
        ...docSnap.data(),
        id: docSnap.id,
      }));
      setIdReviewCases(cases);
    } catch (err) {
      console.error('Admin dashboard error:', err);
      setIdReviewCases([]);
      setIdReviewError(err.message || 'Failed to load review cases.');
    } finally {
      setIdReviewLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab === 'idReview') {
      fetchIdReviewCases();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const handleIdReviewAction = async (userId, action) => {
    if (reviewActionLoading) return;
    const label = action === 'approved' ? 'Approve' : 'Reject';
    if (!window.confirm(`${label} this registration?`)) return;
    setReviewActionLoading(userId);
    try {
      const verUpdate = {
        verificationStatus: action,
        updatedAt: serverTimestamp(),
        reviewedAt: serverTimestamp(),
        reviewedBy: 'admin',
      };
      const userUpdate = {verificationStatus: action};
      if (action === 'approved') userUpdate.verified = true;

      await updateDoc(doc(firestore, 'verifications', userId), verUpdate);
      await updateDoc(doc(firestore, 'users', userId), userUpdate);

      setIdReviewCases((prev) => prev.filter((c) => (c.userId || c.id) !== userId));
      setExpandedReviewId(null);
    } catch (err) {
      console.error('handleIdReviewAction error:', err);
      alert('Action failed: ' + err.message);
    } finally {
      setReviewActionLoading(null);
    }
  };

  const handleExpand = (id) => {
    setExpandedMessageId(expandedMessageId === id ? null : id);
    setReplyText('');
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) return;

    const reply = {
      from: 'admin',
      message: replyText,
      timestamp: new Date().toISOString(),
    };

    await addDoc(
        collection(firestore, `contactMessages/${messageId}/replies`),
        reply,
    );
    await updateDoc(
        doc(firestore, 'contactMessages', messageId),
        {status: 'responded'},
    );

    setReplyText('');
    setExpandedMessageId(null);
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm(
        'Are you sure you want to delete this message? This cannot be undone.',
    )) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, 'contactMessages', messageId));
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  const selectedBreakdownConfig =
    locationBreakdownConfig[locationBreakdownTab];
  const selectedBreakdownEntries = getSortedBreakdown(
      summaryData?.[selectedBreakdownConfig.field] || {},
  );
  const updatedAtLabel = formatStatsTimestamp(
      summaryData?.updatedAt || summaryData?.timestamp,
  );

  return (
    <div className="admin-dashboard">
      <h1>Admin Dashboard</h1>

      <div className="tabs">
        <button
          className={selectedTab === 'summary' ? 'active' : ''}
          onClick={() => setSelectedTab('summary')}
        >
          Summary
        </button>
        <button
          className={selectedTab === 'messages' ? 'active' : ''}
          onClick={() => setSelectedTab('messages')}
        >
          Messages
        </button>
        <button
          className={selectedTab === 'idReview' ? 'active' : ''}
          onClick={() => setSelectedTab('idReview')}
        >
          Registration Review {idReviewCases.length > 0 && `(${idReviewCases.length})`}
        </button>
      </div>

      {selectedTab === 'summary' && (
        <div className="dashboard-summary">
          <div className="summary-header">
            <h2>User Overview</h2>
            <div className="summary-actions">
              {loading && <span>Refreshing stats...</span>}
              {rebuildingStats && <span>Rebuilding master stats...</span>}
              <button
                onClick={async () => {
                  setLoading(true);
                  await fetchSummaryData();
                  setLoading(false);
                }}
                disabled={loading}
              >
                Refresh Stats
              </button>
              <button
                onClick={handleRebuildMasterStats}
                disabled={rebuildingStats}
              >
                Full Rebuild
              </button>
            </div>
          </div>

          {updatedAtLabel && (
            <p className="stats-updated-at">
              Last updated: {updatedAtLabel}
            </p>
          )}

          <ul className="stats-totals">
            <li>Total Users: {summaryData?.totalUsers ?? '-'}</li>
            <li>Active: {summaryData?.byStatus?.active ?? '-'}</li>
            <li>Trial: {summaryData?.byStatus?.trial ?? '-'}</li>
            <li>Cancelled: {summaryData?.byStatus?.cancelled ?? '-'}</li>
            <li>Suspended: {summaryData?.byStatus?.suspended ?? '-'}</li>
            <li>Unknown: {summaryData?.byStatus?.unknown ?? '-'}</li>
          </ul>

          <div className="location-breakdown">
            <div className="location-breakdown-tabs">
              {Object.entries(locationBreakdownConfig).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  className={locationBreakdownTab === key ? 'active' : ''}
                  onClick={() => setLocationBreakdownTab(key)}
                >
                  {config.label}
                </button>
              ))}
            </div>

            <div className="location-breakdown-list">
              {selectedBreakdownEntries.length === 0 ? (
                <p>{selectedBreakdownConfig.empty}</p>
              ) : (
                selectedBreakdownEntries.map(([label, count]) => (
                  <div key={label} className="location-breakdown-row">
                    <span>{label}</span>
                    <strong>{count}</strong>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'messages' && (
        <>
          <p>Review and respond to user-submitted contact messages below.</p>
          {messages.map((msg) => (
            <div key={msg.id} className="message-card">
              <div className="collapsed-view">
                <div
                  onClick={() => handleExpand(msg.id)}
                  style={{flex: 1, cursor: 'pointer'}}
                >
                  <strong>{msg.title || '(No Title)'}</strong>{' '}
                  {msg.message.slice(0, 50)}...
                  <div className="meta">
                    {msg.firstName} {msg.lastName} ({msg.rank},{' '}
                    {msg.assignment}, {msg.agency}){' '}
                    {msg.timestamp?.toDate
                      ? msg.timestamp.toDate().toLocaleString()
                      : '(No Date)'}{' '}
                    Status: {msg.status}
                  </div>
                </div>
                <button onClick={() => handleDelete(msg.id)} title="Delete">
                  Delete
                </button>
              </div>

              {expandedMessageId === msg.id && (
                <div className="expanded-view">
                  <p>{msg.message}</p>
                  {msg.replies?.length > 0 && (
                    <div className="admin-replies">
                      <strong>Your Reply:</strong>
                      {msg.replies.map((reply) => (
                        <div
                          key={reply.id}
                          style={{
                            marginBottom: '8px',
                            fontStyle: 'italic',
                            paddingLeft: '10px',
                            borderLeft: '2px solid #007bff',
                          }}
                        >
                          <p>{reply.message}</p>
                          <p
                            style={{
                              fontSize: '0.8em',
                              color: '#777',
                            }}
                          >
                            {reply.timestamp
                              ? new Date(reply.timestamp).toLocaleString()
                              : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    placeholder="Write your reply"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  ></textarea>
                  <button onClick={() => handleReply(msg.id)}>
                    Send Reply
                  </button>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {selectedTab === 'idReview' && (
        <>
          <div className="review-header">
            <p>
              Registrations pending manual review. Approve or reject each
              account.
            </p>
            <button onClick={fetchIdReviewCases} disabled={idReviewLoading}>
              {idReviewLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {idReviewLoading && <p>Loading review cases...</p>}

          {!idReviewLoading && idReviewError && (
            <p className="review-error">
              Failed to load review cases: {idReviewError}
            </p>
          )}

          {!idReviewLoading && !idReviewError && idReviewCases.length === 0 && (
            <p>No cases pending review.</p>
          )}

          {idReviewCases.map((c) => {
            const userId = c.userId || c.id;
            const name = [
              c.firstName,
              c.middleInitial,
              c.lastName,
            ].filter(Boolean).join(' ') || c.email || userId;
            const isExpanded = expandedReviewId === userId;
            const isActioning = reviewActionLoading === userId;
            const firstSubmitted = c.createdAt?.toDate ?
              c.createdAt.toDate().toLocaleString() :
              '';
            const sentForReview = c.updatedAt?.toDate ?
              c.updatedAt.toDate().toLocaleString() :
              '';

            return (
              <div key={userId} className="message-card">
                <div className="collapsed-view">
                  <div
                    style={{flex: 1, cursor: 'pointer'}}
                    onClick={() => setExpandedReviewId(
                        isExpanded ? null : userId,
                    )}
                  >
                    <strong>{name}</strong>
                    <span className="review-badge">Review</span>
                    <div className="meta">
                      {firstSubmitted ?
                        'First submitted: ' + firstSubmitted :
                        'First submitted: -'}
                      {sentForReview ? ' | Sent for review: ' + sentForReview : ''}
                    </div>
                    <div className="meta">
                      {c.country || 'Country not provided'} {' | '}
                      {c.stateProvince || 'Region not provided'} {' | '}
                      {c.email} {' | '} {c.phoneNumber}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="expanded-view">
                    <p>
                      <strong>Registered name:</strong> {name}
                      {' | '}
                      <strong>Email:</strong> {c.email || '-'}
                      {' | '}
                      <strong>Phone:</strong> {c.phoneNumber || '-'}
                    </p>

                    <p>
                      <strong>Country:</strong> {c.country || '-'}
                      {' | '}
                      <strong>Region:</strong> {c.stateProvince || '-'}
                      {' | '}
                      <strong>City:</strong> {c.city || '-'}
                    </p>

                    <p>
                      <strong>Review reason:</strong>{' '}
                      {c.reviewReason || 'Registration submitted for admin review'}
                    </p>

                    <div className="review-actions">
                      <button
                        onClick={() => handleIdReviewAction(userId, 'approved')}
                        disabled={isActioning}
                      >
                        {isActioning ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleIdReviewAction(userId, 'rejected')}
                        disabled={isActioning}
                      >
                        {isActioning ? 'Processing...' : 'Reject'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
